import {HttpResponse} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {map, Observable} from "rxjs";
import {EditChange, EditLogEntry, InferContextRequest, InferContextResponse, PageGeometry, PageWords,
    ProvenanceFieldDef, ScrapeJobStatus, ScrapeResult} from "../models/scrape-result";
import {ScrapeJobPage} from "../models/scrape-job";
import {ImportResult} from "../models/import-result";
import {DetectedDocumentType, ScraperApiService} from "../generated-sources/api/scraper";
import {ScraperDocumentType} from "../models/scraper-document-type";
import {ScrapeExportRequest} from "../models/scrape-export";

export {DetectedDocumentType};

/** Values `detectDocumentTypes` can return beyond the three real `ScraperDocumentType`s —
 * see US-1337's document_type_detector.py, which never guesses when it isn't one of those. */
export const RECOGNIZED_OUT_OF_SCOPE = "recognized_out_of_scope";
export const UNDETECTED = "undetected";

export type DetectedDocumentTypeValue =
    ScraperDocumentType | typeof RECOGNIZED_OUT_OF_SCOPE | typeof UNDETECTED;

@Injectable({providedIn: "root"})
export class ScraperService {
    private readonly scraperApiService = inject(ScraperApiService);

    /**
     * Upload a PDF into the async scrape-job + scraper-viewer flow, whatever kind of document it is.
     * One endpoint and one method for every document type: the server discriminates on
     * `documentType`, so the active-job cap and the job lifecycle apply identically to all of them.
     * Defaults to a GP quarterly report, matching the contract's own default.
     */
    uploadPdf(file: File, documentType: ScraperDocumentType = ScraperDocumentType.QUARTERLY_REPORT): Observable<{ jobId: number }> {
        return this.scraperApiService.uploadPdf(file, documentType);
    }

    getScrapeJobStatus(jobId: number): Observable<ScrapeJobStatus> {
        return this.scraperApiService.getScrapeJobStatus(jobId);
    }

    getScrapeResult(jobId: number): Observable<ScrapeResult> {
        return this.scraperApiService.getScrapeResult(jobId);
    }

    /** Page sizes, so the viewer can lay the document out before any page image has arrived. */
    getPageGeometry(jobId: number): Observable<PageGeometry[]> {
        return this.scraperApiService.getScrapePages(jobId).pipe(map(response => response.pages));
    }

    /**
     * One rendered page.
     *
     * Requested through `HttpClient` rather than by pointing an `<img src>` at the endpoint: the
     * desk authenticates with a bearer token added by an interceptor, and an `<img>` sends no
     * token. The browser's HTTP cache still applies, so a page already fetched costs nothing.
     */
    getPageImage(jobId: number, pageNumber: number): Observable<Blob> {
        return this.scraperApiService.getScrapePageImage(jobId, pageNumber);
    }

    /** The word layer, fetched the first time document search or "Add from PDF" is used. */
    getWords(jobId: number): Observable<PageWords[]> {
        return this.scraperApiService.getScrapeWords(jobId).pipe(map(response => response.pages));
    }

    approveScrapeImport(jobId: number, dataset?: string, refDocument?: string): Observable<ImportResult> {
        return this.scraperApiService.approveScrapeImport(jobId, {dataset, refDocument});
    }

    getScraperJobs(params: { search?: string; userName?: string; activeOnly?: boolean; page?: number; size?: number; sort?: string }): Observable<ScrapeJobPage> {
        return this.scraperApiService.getScraperJobs(
            params.search || undefined,
            params.userName || undefined,
            params.activeOnly,
            params.page,
            params.size,
            params.sort
        );
    }

    cancelJob(jobId: number): Observable<void> {
        return this.scraperApiService.cancelScrapeJob(jobId);
    }

    getFieldSchema(): Observable<ProvenanceFieldDef[]> {
        return this.scraperApiService.getFieldSchema();
    }

    editProvenance(jobId: number, version: number, change: EditChange): Observable<ScrapeResult> {
        return this.scraperApiService.applyProvenanceEdit(jobId, {version, change});
    }

    resetAllEdits(jobId: number): Observable<ScrapeResult> {
        return this.scraperApiService.resetEdits(jobId);
    }

    getEditLog(jobId: number, recordId?: string): Observable<EditLogEntry[]> {
        return this.scraperApiService.getEditLog(jobId, recordId);
    }

    inferContext(jobId: number, request: InferContextRequest): Observable<InferContextResponse> {
        return this.scraperApiService.inferContext(jobId, request);
    }

    /**
     * Downloads a finished job's extraction.
     *
     * Observed as `response` rather than `body` on purpose: the server names the file, and the name
     * carries the audit context (source document, document type, job id, whether the copy is edited,
     * when it was taken). The default `body` observation yields only the `Blob` and throws that name
     * away, leaving the browser to invent one — or us to rebuild the server's naming rules here, free
     * to drift from them.
     */
    exportScrapeResult(request: ScrapeExportRequest): Observable<HttpResponse<Blob>> {
        const sections = request.sections.join(",");

        // One job goes to the single-job endpoint and comes back as the file itself; several go to the
        // bulk endpoint and come back as a zip. Deciding here rather than at each call site keeps
        // the caller's intent — "download these jobs" — free of that transport detail, and stops a
        // single-row export arriving as a zip the user then has to unpack.
        return request.jobIds.length === 1
            ? this.scraperApiService.exportScrapeResult(
                request.jobIds[0], request.format, sections, request.version, "response")
            : this.scraperApiService.exportScrapeResults(
                request.jobIds, request.format, sections, request.version, "response");
    }

    /**
     * Classifies a batch of filenames by name alone (US-1337/US-1339) — no upload, no job
     * created. Powers the upload dialog's "Detect document type" mode: shows what each file
     * would classify as before spending the cost of actually uploading it.
     *
     * A `documentType` can be `qr`/`cas`/`fs` (one file can match all three — a GP reporting
     * package does), {@link RECOGNIZED_OUT_OF_SCOPE}, or {@link UNDETECTED} — never silently
     * defaulted to `qr`. Each carries the evidence that produced it, which is what lets the
     * dialog show *why* a file was classified as it was rather than only the verdict.
     */
    detectDocumentTypes(filenames: string[]): Observable<Record<string, DetectedDocumentType[]>> {
        return this.scraperApiService.detectDocumentType({filenames});
    }
}
